/**
 * NoteList Component
 *
 * @component
 * @description This component displays a list of notes in an accordion-style container.
 * It allows adding new notes and managing the visibility of existing notes.
 *
 * @param {NoteData[]} props.notes - An array of NoteData objects representing the notes to be displayed.
 * @returns {JSX.Element} - A JSX element representing the NoteList component.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { isNull } from 'lodash';
import { useTabsMenu } from 'components/TabsNavigation/hooks';
import { LayoutContext } from 'context';
import useNotesContext from 'context/NotesContext';
import { hasNoteContent } from 'services/hooks/notes/utilities';
import { useUserUtils } from 'utilities/userUtils/useUserUtils';
import { ApproachActionOption } from '../../pages/ValuationsAllocation/components';
import { Tab, TabsNavigation } from '../TabsNavigation';
import Note from './note/Note';
import { StyledButton, StyledTextField } from './styles';
import { NoteData, NoteListProps } from './types';

const INTERNAL_NOTES_VIEW = 'internal';
const CLIENT_NOTES_VIEW = 'client';
type NotesView = typeof INTERNAL_NOTES_VIEW | typeof CLIENT_NOTES_VIEW;

const NoteList = (props: NoteListProps) => {
  const { pageType, pageTypeId, onAddNote, onDeleteNote, onUpdateNotes } = useNotesContext();
  const { viewOnlyUser } = useContext(LayoutContext);
  const { isStaffUser } = useUserUtils();
  const isStaff = isStaffUser();
  const { notes, isDisabled = false, appBarSx } = props;
  const actionsAreDisabled = isDisabled || viewOnlyUser;
  const [visibleNotes, setVisibleNotes] = useState<NoteData[]>([]);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [activeView, setActiveView] = useState<NotesView>(CLIENT_NOTES_VIEW);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasAutoAddedRef = useRef<Set<string>>(new Set());
  const shouldFocusNewNoteRef = useRef(false);
  const [activeTabIdToSelect, setActiveTabIdToSelect] = useState<string | null>(null);

  const { handleMenuOpen, handleMenuClose, menuAnchorEl } = useTabsMenu();

  const handleOnAddNote = useCallback(() => {
    if (pageType == null || pageTypeId == null) {
      return;
    }
    const isInternal = activeView === INTERNAL_NOTES_VIEW;
    onAddNote?.({
      pageType,
      pageTypeId: typeof pageTypeId === 'number' ? pageTypeId : Number(pageTypeId),
      is_internal: isInternal,
    });
  }, [onAddNote, pageType, pageTypeId, activeView]);

  useEffect(() => {
    if (isNull(notes) || actionsAreDisabled || !onAddNote) {
      return;
    }
    const visibleCount = notes.filter(n => {
      if (n.is_deleted) {
        return false;
      }
      if (!isStaff && n.is_internal) {
        return false;
      }
      if (isStaff && (activeView === INTERNAL_NOTES_VIEW ? !n.is_internal : n.is_internal)) {
        return false;
      }
      return !!n.temp_id || hasNoteContent(n.content);
    }).length;

    const hasPendingDraft = (notes ?? []).some(n => !n.is_deleted && !!n.temp_id);

    // Match fresh-page UX: when nothing is visible (including after deleting the last
    // saved note), offer an empty draft. Reset the guard whenever visibility drops to zero
    // so deleting the sole draft can seed again.
    if (visibleCount === 0) {
      hasAutoAddedRef.current.delete(activeView);
    }
    if (hasAutoAddedRef.current.has(activeView)) {
      return;
    }
    if (visibleCount === 0 && !hasPendingDraft) {
      hasAutoAddedRef.current = new Set([...hasAutoAddedRef.current, activeView]);
      handleOnAddNote();
    }
  }, [notes, activeView, isStaff, actionsAreDisabled, onAddNote, handleOnAddNote]);

  useEffect(() => {
    if (!isNull(notes)) {
      const tempVisibleNotes = notes.filter(note => {
        if (note.is_deleted) return false;
        if (!isStaff) return !note.is_internal;
        return activeView === INTERNAL_NOTES_VIEW ? !!note.is_internal : !note.is_internal;
      });
      setVisibleNotes(tempVisibleNotes);
    }
  }, [notes, activeView, isStaff]);

  const getNoteLabel = (note: NoteData, i: number) => note.name || `Note ${i + 1}`;

  const handleDoubleClick = useCallback((note: NoteData) => {
    if (actionsAreDisabled) {
      return;
    }
    const noteId = note.id ? `${note.id}` : `${note.temp_id}`;
    setEditingNoteId(noteId);
    setEditingValue(note.name || '');
  }, [actionsAreDisabled]);

  const handleSaveEdit = useCallback((note: NoteData) => {
    if (!onUpdateNotes) {
      return;
    }
    const updatedNote = { ...note, name: editingValue.trim() || null };
    onUpdateNotes(updatedNote);
    setEditingNoteId(null);
    setEditingValue('');
  }, [editingValue, onUpdateNotes]);

  const handleCancelEdit = useCallback(() => {
    setEditingNoteId(null);
    setEditingValue('');
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent, note: NoteData) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveEdit(note);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelEdit();
    }
  }, [handleSaveEdit, handleCancelEdit]);

  useEffect(() => {
    if (editingNoteId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingNoteId]);

  useEffect(() => {
    // only allow a single temp note at any given time...

    const hasTempNote = visibleNotes.some(i => i.temp_id);
    const noteTabs: Tab[] = visibleNotes.map((note, i) => {
      const noteId = note.id ? `${note.id}` : `${note.temp_id}`;
      const isEditing = editingNoteId === noteId;
      const displayLabel = getNoteLabel(note, i);

      return {
        content: <Note key={note.id ?? note.temp_id} note={note} isDisabled={actionsAreDisabled} autoFocus={!!note.temp_id && shouldFocusNewNoteRef.current} />,
        disabled: actionsAreDisabled,
        disableRipple: isEditing,
        id: noteId,
        label: isEditing ? (
          <StyledTextField
            inputRef={inputRef}
            value={editingValue}
            onChange={e => setEditingValue(e.target.value)}
            onBlur={() => handleSaveEdit(note)}
            onKeyDown={e => handleKeyDown(e, note)}
            onClick={e => e.stopPropagation()}
            onDoubleClick={e => e.stopPropagation()}
            variant="standard"
          />
        ) : (
          <StyledButton
            onDoubleClick={() => handleDoubleClick(note)}
            disabled={actionsAreDisabled}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              cursor: actionsAreDisabled ? 'default' : 'pointer',
              color: 'inherit',
            }}>
            {displayLabel}
          </StyledButton>
        ),
        menuItems: [
          ...(onDeleteNote
            ? [
              <ApproachActionOption<NoteData, never>
                key={`delete-${note.id ?? note.temp_id}`}
                actionText="Delete"
                argumentsList={[note]}
                tooltipMessage="Delete Note from the system"
                onClick={() => {
                  onDeleteNote?.({ ...note, is_deleted: true });
                  handleMenuClose();
                }}
              />,
            ]
            : []),
        ],
      };
    });

    if (onAddNote) {
      noteTabs.push({
        id: 'add-button',
        content: <></>,
        disabled: false,
        isActionTab: true,
        label: (
          <Tooltip title={hasTempNote ? 'Only one pending note allowed' : ''}>
            <span>
              <Button onClick={() => {
                shouldFocusNewNoteRef.current = true;
                handleOnAddNote();
              }}
              disabled={hasTempNote || actionsAreDisabled}>
                +
              </Button>
            </span>
          </Tooltip>
        ),
      });
    }

    if (visibleNotes.some(n => n.temp_id) && shouldFocusNewNoteRef.current) {
      const tempNote = visibleNotes.find(n => n.temp_id);
      if (tempNote?.temp_id) setActiveTabIdToSelect(String(tempNote.temp_id));
      shouldFocusNewNoteRef.current = false;
    }

    setTabs(noteTabs);

    // exhaustive dependencies are not needed here and result in degraded performance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleNotes, actionsAreDisabled, editingNoteId, editingValue, handleDoubleClick, handleSaveEdit, handleKeyDown]);

  const handleViewChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newView: NotesView | null) => {
      if (newView !== null) {
        setActiveView(newView);
      }
    },
    []
  );

  const notesViewToggle = useMemo(
    () =>
      isStaff ? (
        <ToggleButtonGroup
          value={activeView}
          exclusive
          onChange={handleViewChange}
          size="small"
          sx={{ height: '28px' }}>
          <ToggleButton
            value={CLIENT_NOTES_VIEW}
            sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5 }}>
            Client
          </ToggleButton>
          <ToggleButton
            value={INTERNAL_NOTES_VIEW}
            sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5 }}>
            Internal
          </ToggleButton>
        </ToggleButtonGroup>
      ) : null,
    [isStaff, activeView, handleViewChange]
  );

  return (
    <TabsNavigation
      menuAnchorEl={menuAnchorEl}
      mountOnActive
      handleMenuClose={handleMenuClose}
      handleMenuOpen={handleMenuOpen}
      relativeDrawer
      tabs={tabs}
      appBarSx={appBarSx}
      rightAction={notesViewToggle}
      activeTabId={activeTabIdToSelect}
      onActiveTabIdApplied={() => setActiveTabIdToSelect(null)}
    />
  );
};

export default NoteList;
